import { useState, useEffect } from 'react';
import { getGoogleCustomSearchApiKey } from '@/lib/api-keys';

// IDs dos Custom Search Engines
export const CSE_ID_BOOKING = 'd13cfc7376f1c4a3c';
export const CSE_ID_GENERAL = 'c380a2b220ccf47e1';

interface GoogleSearchResult {
  title: string;
  link: string;
  snippet: string;
  pagemap?: {
    metatags?: Array<{
      [key: string]: string;
    }>;
  };
}

interface GoogleApiError {
  code: number;
  message: string;
  errors: Array<{
    message: string;
    domain: string;
    reason: string;
  }>;
  status: string;
}

interface GoogleSearchResponse {
  items?: GoogleSearchResult[];
  searchInformation?: {
    totalResults: string;
    searchTime: number;
    formattedSearchTime: string;
  };
  error?: GoogleApiError;
  spelling?: {
    correctedQuery: string;
    htmlCorrectedQuery: string;
  };
}

export interface SearchResult {
  title: string;
  url: string;
  description: string;
}

// Função para log detalhado de erros
const logApiError = (error: any, context: { query: string; cseId: string; }) => {
  console.group('🔴 Erro na API do Google Search');
  console.log('Timestamp:', new Date().toISOString());
  console.log('Modo:', context.cseId === CSE_ID_BOOKING ? 'Booking.com' : 'Geral');
  
  if (error.error) {
    // Erro da API Google
    console.log('Código:', error.error.code);
    console.log('Status:', error.error.status);
    console.log('Mensagem:', error.error.message);
    console.table(error.error.errors || []);
  } else {
    // Erro genérico
    console.log('Tipo:', error.name);
    console.log('Mensagem:', error.message);
    console.log('Stack:', error.stack);
  }
  
  console.log('Contexto da requisição:', {
    query: context.query,
    cseId: context.cseId,
    timestamp: new Date().toISOString()
  });
  
  console.groupEnd();
};

// Função para gerar mensagem de erro amigável
const getErrorMessage = (error: any): string => {
  // Se for um erro da API com detalhes
  if (error.error?.errors?.[0]) {
    const apiError = error.error.errors[0];
    switch (apiError.reason) {
      case 'invalid':
        return 'Parâmetros de busca inválidos. Verifique os termos utilizados.';
      case 'keyInvalid':
        return 'API Key inválida. Verifique suas configurações.';
      case 'dailyLimitExceeded':
        return 'Limite diário de buscas excedido. Tente novamente amanhã.';
      case 'quotaExceeded':
        return 'Cota de requisições excedida. Tente novamente mais tarde.';
      default:
        return `Erro: ${apiError.message}`;
    }
  }

  // Se for um erro HTTP comum
  switch (error.error?.code) {
    case 400:
      return 'Requisição inválida. Verifique os termos de busca.';
    case 401:
      return 'Não autorizado. Verifique sua API Key.';
    case 403:
      return 'Acesso negado. Verifique suas permissões.';
    case 429:
      return 'Muitas requisições. Aguarde um momento e tente novamente.';
    case 500:
      return 'Erro no servidor do Google. Tente novamente mais tarde.';
    default:
      return error.message || 'Erro desconhecido na busca';
  }
};

export function useGoogleApi() {
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    const apiKey = getGoogleCustomSearchApiKey();
    setIsConfigured(!!apiKey);
  }, []);

  const searchPages = async (
    query: string,
    cseId: string
  ): Promise<SearchResult[]> => {
    const apiKey = getGoogleCustomSearchApiKey();
    
    if (!apiKey) {
      throw new Error('API Key do Google não configurada');
    }

    const isBookingSearch = cseId === CSE_ID_BOOKING;
    const searchUrl = new URL('https://www.googleapis.com/customsearch/v1');
    
    const params = {
      key: apiKey,
      cx: cseId,
      q: query,
      num: '10',
      lr: 'lang_pt',    // Preferência por resultados em português
      gl: 'br',         // Localização: Brasil
      filter: '1',      // Remove resultados duplicados
      safe: 'off',      // Desativa SafeSearch
      dateRestrict: 'y1', // Resultados do último ano
      // Parâmetros específicos para busca no Booking.com
      ...(isBookingSearch && {
        siteSearch: 'booking.com',
        siteSearchFilter: 'i'
      })
    };

    // Adiciona os parâmetros à URL
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchUrl.searchParams.append(key, value);
      }
    });

    try {
      console.group('🔍 Iniciando busca Google');
      console.log('Termo:', query);
      console.log('Modo:', isBookingSearch ? 'Booking.com' : 'Geral');
      // Oculta a API Key no log
      console.log('URL:', searchUrl.toString().replace(apiKey, '***'));
      console.table(params);

      const response = await fetch(searchUrl.toString());
      const data: GoogleSearchResponse = await response.json();

      console.group('📥 Resposta da API');
      if (data.spelling?.correctedQuery) {
        console.log('✍️ Sugestão de correção:', {
          original: query,
          sugestão: data.spelling.correctedQuery
        });
      }
      console.log('Total de resultados:', data.searchInformation?.totalResults || 0);
      console.log('Tempo de busca:', data.searchInformation?.searchTime || 0, 'segundos');
      console.groupEnd();

      if (data.error) {
        logApiError(data, { query, cseId });
        throw new Error(getErrorMessage(data));
      }

      if (!data.items || data.items.length === 0) {
        // Se houver sugestão de correção, tenta novamente com o termo corrigido
        if (data.spelling?.correctedQuery && !query.includes('booking.com')) {
          console.log('🔄 Tentando novamente com termo corrigido');
          return searchPages(data.spelling.correctedQuery, cseId);
        }
        console.log('⚠️ Nenhum resultado encontrado');
        return [];
      }

      console.log('✅ Busca concluída:', {
        resultados: data.items.length,
        totalEncontrado: data.searchInformation?.totalResults
      });
      console.groupEnd();

      return data.items.map(item => ({
        title: item.title,
        url: item.link,
        description: item.snippet
      }));
    } catch (error) {
      logApiError(error, { query, cseId });
      throw new Error(getErrorMessage(error));
    }
  };

  return {
    isConfigured,
    searchPages
  };
} 